import { usePage } from "@inertiajs/react"
import React, { useEffect } from "react"
import { IntercomDisabledAlert } from "./components/intercom-disabled-alert"
import { AdwordsConversionScript } from "./scripts/adwords-conversion"
import {
  reportAndReload,
  clearReloadGuard
} from "./layout-config-sentry-context"

const RELOAD_KEY = "inertia_footer_error_reload_count"

/**
 * Error boundary for InertiaPageFooter. Same recovery strategy as the header:
 * report to Sentry, reload once, then render null if still crashing.
 */
class InertiaPageFooterErrorBoundary extends React.Component {
  constructor(props) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError() {
    return { hasError: true }
  }

  componentDidCatch(error, info) {
    const reloading = reportAndReload(
      error,
      info,
      this.props.pageContext,
      RELOAD_KEY
    )
    if (!reloading) {
      this.setState({ hasError: true })
    }
  }

  render() {
    if (this.state.hasError) return null
    return this.props.children
  }
}

/**
 * InertiaPageFooter renders layout elements that should appear after the main content.
 * This includes bottom alerts, conversion tracking scripts, and modals.
 */
export function InertiaPageFooter() {
  const page = usePage()
  const {
    props: { layoutConfig }
  } = page

  useEffect(() => {
    clearReloadGuard(RELOAD_KEY)
  }, [])

  const pageContext = {
    layoutConfig,
    url: page.url,
    component: page.component,
    version: page.version
  }

  return (
    <InertiaPageFooterErrorBoundary pageContext={pageContext}>
      <InertiaPageFooterContent layoutConfig={layoutConfig} />
    </InertiaPageFooterErrorBoundary>
  )
}

function InertiaPageFooterContent({ layoutConfig }) {
  return (
    <>
      {layoutConfig.components.intercomDisabledAlert.visible && (
        <IntercomDisabledAlert />
      )}

      {layoutConfig.components.adwordsScript.visible && (
        <AdwordsConversionScript
          {...layoutConfig.components.adwordsScript.data}
        />
      )}
    </>
  )
}
